import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { typeLabel } from "@/lib/movieData";

interface MovieCardProps {
  movie: Movie;
  variant?: "grid" | "wide";
}

export default function MovieCard({ movie, variant = "grid" }: MovieCardProps) {
  if (variant === "wide") {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-shadow hover:border-primary-300"
        data-product-shop
      >
        <div className="relative w-24 h-32 md:w-28 md:h-40 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={movie.poster}
            alt={`${movie.title}中文字幕封面`}
            title={`${movie.title} 中文字幕`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col">
          <h3 className="font-heading text-base font-bold text-foreground-950 line-clamp-1 group-hover:text-primary-700">
            {movie.title}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
              {typeLabel(movie.type)}
            </span>
            <span>{movie.year}</span>
            <span className="text-accent-600 font-semibold">{movie.rating.toFixed(1)} 分</span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">
            {movie.summary}
          </p>
          <div className="mt-auto pt-2 flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-time-line"></i>
            字幕更新于 {movie.updatedAt}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      data-product-shop
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={movie.poster}
          alt={`${movie.title}中文字幕封面`}
          title={`${movie.title} 中文字幕`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-primary-600/90 px-2 py-0.5 text-[11px] font-medium text-background-50 dark:text-foreground-950">
          {typeLabel(movie.type)}
        </span>
        <span className="absolute right-2 top-2 rounded-md bg-accent-500/95 px-2 py-0.5 text-[11px] font-semibold text-background-50 dark:text-foreground-950">
          {movie.rating.toFixed(1)}
        </span>
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-foreground-950/80 to-transparent px-2 pb-2 pt-6 text-[11px] text-background-50">
          <i className="ri-translate-2"></i>
          {movie.subtitleNote.slice(0, 12)}…
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <h3 className="font-heading text-sm font-bold text-foreground-950 line-clamp-1 group-hover:text-primary-700 md:text-base">
          {movie.title}
        </h3>
        <div className="flex flex-wrap gap-1">
          {movie.genres.slice(0, 2).map((genre) => (
            <span
              key={genre}
              className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900"
            >
              {genre}
            </span>
          ))}
        </div>
        <p className="text-xs leading-relaxed text-foreground-600 line-clamp-2">
          {movie.summary}
        </p>
        <div className="mt-auto flex items-center justify-between pt-1 text-[11px] text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {movie.updatedAt}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line"></i>
            {(movie.views / 10000).toFixed(1)}万
          </span>
        </div>
      </div>
    </Link>
  );
}